/**
 * CustomScrollbar - Unified scrollbar component for horizontal and vertical scrolling
 * @param {Object} scrollRef - React ref to the scrollable element
 * @param {string} direction - 'horizontal' or 'vertical'
 * @param {boolean} show - Optional visibility control (default: auto-detect based on overflow)
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const CustomScrollbar = ({ scrollRef, direction = 'horizontal', show = true }) => {
  const isVertical = direction === 'vertical';
  const [scrollState, setScrollState] = useState({ position: 0, size: 30 });
  const [showIndicator, setShowIndicator] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef(null);
  const dragStartRef = useRef(null);

  const updateScrollIndicator = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    
    // If show is explicitly false, don't show the scrollbar
    if (!show) {
      setShowIndicator(false);
      return;
    }
    
    // Get scroll properties based on direction
    const scrollPos = isVertical ? el.scrollTop : el.scrollLeft;
    const scrollTotal = isVertical ? el.scrollHeight : el.scrollWidth;
    const clientSize = isVertical ? el.clientHeight : el.clientWidth;
    
    const canScroll = scrollTotal > clientSize;
    setShowIndicator(canScroll);
    
    if (canScroll) {
      const maxScroll = scrollTotal - clientSize;
      const scrollPercent = maxScroll > 0 ? scrollPos / maxScroll : 0;
      const indicatorSize = Math.max(30, (clientSize / scrollTotal) * clientSize);
      const maxPosition = clientSize - indicatorSize;
      
      setScrollState({
        position: scrollPercent * maxPosition,
        size: indicatorSize
      });
    }
  }, [scrollRef, isVertical, show]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollIndicator();
    el.addEventListener('scroll', updateScrollIndicator);
    window.addEventListener('resize', updateScrollIndicator);
    
    return () => {
      el.removeEventListener('scroll', updateScrollIndicator);
      window.removeEventListener('resize', updateScrollIndicator);
    };
  }, [scrollRef, updateScrollIndicator, show]);

  // Scroll to position based on click coordinate
  const scrollToPosition = useCallback((clientPos) => {
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el || !track) return;

    const rect = track.getBoundingClientRect();
    const clickPos = isVertical
      ? Math.max(0, Math.min(clientPos - rect.top, rect.height))
      : Math.max(0, Math.min(clientPos - rect.left, rect.width));
    const trackSize = isVertical ? rect.height : rect.width;
    const scrollPercent = clickPos / trackSize;
    
    const scrollTotal = isVertical ? el.scrollHeight : el.scrollWidth;
    const clientSize = isVertical ? el.clientHeight : el.clientWidth;
    const maxScroll = scrollTotal - clientSize;
    
    if (isVertical) {
      el.scrollTop = scrollPercent * maxScroll;
    } else {
      el.scrollLeft = scrollPercent * maxScroll;
    }
  }, [scrollRef, isVertical]);

  // Handle click on track (not thumb) to scroll
  const handleTrackClick = (e) => {
    if (e.target === trackRef.current) {
      scrollToPosition(isVertical ? e.clientY : e.clientX);
    }
  };

  // Handle drag start on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = scrollRef.current;
    if (el) {
      const clientPos = isVertical 
        ? (e.clientY || e.touches?.[0]?.clientY)
        : (e.clientX || e.touches?.[0]?.clientX);
      dragStartRef.current = {
        mousePos: clientPos,
        scrollPos: isVertical ? el.scrollTop : el.scrollLeft
      };
    }
    setIsDragging(true);
  };

  // Handle drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track || !dragStartRef.current) return;

      const clientPos = isVertical
        ? (e.clientY || e.touches?.[0]?.clientY)
        : (e.clientX || e.touches?.[0]?.clientX);
      const deltaPos = clientPos - dragStartRef.current.mousePos;
      
      const trackSize = isVertical 
        ? track.getBoundingClientRect().height 
        : track.getBoundingClientRect().width;
      const scrollTotal = isVertical ? el.scrollHeight : el.scrollWidth;
      const clientSize = isVertical ? el.clientHeight : el.clientWidth;
      const scrollRatio = (scrollTotal - clientSize) / (trackSize - scrollState.size);
      
      const newScrollPos = dragStartRef.current.scrollPos + (deltaPos * scrollRatio);
      if (isVertical) {
        el.scrollTop = newScrollPos;
      } else {
        el.scrollLeft = newScrollPos;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, scrollRef, isVertical, scrollState.size]);

  if (!showIndicator) return null;

  // Track and thumb styles based on direction
  const trackClassName = isVertical
    ? "w-3 bg-site-bg-alt relative cursor-pointer select-none flex-shrink-0"
    : "h-3 bg-white relative cursor-pointer select-none";

  const thumbStyle = isVertical
    ? { 
        height: `${scrollState.size}px`,
        transform: `translateY(${scrollState.position}px)`,
        width: '100%'
      }
    : { 
        width: `${scrollState.size}px`,
        transform: `translateX(${scrollState.position}px)`,
        height: '100%'
      };

  const thumbClassName = isVertical
    ? "bg-site-text-muted absolute left-0 cursor-grab active:cursor-grabbing"
    : "bg-site-text-muted absolute top-0 cursor-grab active:cursor-grabbing";

  return (
    <div 
      ref={trackRef}
      className={trackClassName}
      onClick={handleTrackClick}
    >
      <div 
        className={thumbClassName}
        style={thumbStyle}
        onMouseDown={handleThumbMouseDown}
        onTouchStart={handleThumbMouseDown}
      />
    </div>
  );
};

export default CustomScrollbar;
